import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import moment, { Moment } from "moment";
import shpwriteZip from "@/lib/shp-write/src/zip";

import fileDownload from "js-file-download";

//Material-UI Core Components
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useMediaQuery, useTheme } from "@mui/material";

//OL
import OlGeoJSON from "ol/format/GeoJSON";
import OlFeature from "ol/Feature";
import OlGeometry from "ol/geom/Geometry";
import OlLineString from "ol/geom/LineString";

//Custom Components
import AppPage from "@/ui/AppPage/AppPage";
import Dashboard from "@/views/HomePage/Dashboard";
import { GridContainer, GridItem } from "@/ui/Grid";
import ReportsMap from "./ReportsMap";
import ReportsStazeTable from "./ReportsStazeTable";
import model from "@/models/staze";
import dataController from "@/lib/dataController";
import LoaderContext from "@/components/LoaderContext/LoaderContext";
import { Permissions } from "@/lib/permissions";
import { exportTableToExcel } from "@/lib/exportExcel";

// Images
import MapsImage from "@/images/card-header-maps.jpg";
import ExcelImage from "@/images/card-header-excel.jpg";

// Types
import { DCRecord, IDataController } from "@/@types/lib/dataController";
import { LoaderContextType } from "@/@types/context/LoaderContext";

function ReportsPage() {
  const loaderContext = useContext(LoaderContext) as LoaderContextType;

  const { t } = useTranslation();

  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));

  const handleDownloadShape = () => {
    const dc: IDataController = new dataController(model);
    loaderContext.toggleLoading(true);
    dc.GetData("staze?export=shape")
      .then((resp) => {
        if (resp.success) {
          const records = resp.data as DCRecord[];

          const features = records.map((x) => {
            //@ts-ignore
            const f = x.geom as OlFeature<OlGeometry>;
            f.setId(x.id as number);
            f.set("id", x.id);
            f.set("tip_id", x.staza_tip_id);
            f.set("hps_broj_staze", x.hps_broj_staze);
            f.set("hps_drustvo", x.hps_drustvo);
            f.set("mark_status", x.hgss_markacija_status_id);
            f.set("dt_provjere", x.hgss_datum_provjere);
            f.set("hps_nap", x.hps_napomena);
            f.set("hgss_nap", x.hgss_napomena);
            f.set("hgss_prsif", x.hgss_projekt_sifra);
            f.set("duljina_m", x.duljina_m);
            f.set("delta_h_m", x.delta_h_m);
            f.set("vrijeme_min", x.vrijeme_min);
            f.set("hps_naziv", x.hps_naziv);
            f.set("hps_naziv_ttoken", x.hps_naziv_ttoken);
            f.set("podrucje", x.podrucje);
            f.set("planina", x.planina);
            f.set("active", x.active);
            f.set("hps_oznaka", x.hps_oznaka);
            f.set("zahtjevno_komentar", x.zahtjevno_komentar);
            f.set("owner_organisation_id", x.owner_organisation_id);
            f.set("data_source_id", x.data_source_id);
            f.set("pocetak", x.pocetak);
            f.set("kraj", x.kraj);
            f.set("godina_obnove", x.godina_obnove);

            return f;
          });

          const geoJSONformat = new OlGeoJSON({});

          const encodedFeatures = geoJSONformat.writeFeatures(features, {
            dataProjection: "EPSG:4326",
            featureProjection: "EPSG:3857",
            decimals: 7,
          });

          const geoJSON = JSON.parse(encodedFeatures);
          geoJSON.crs = {
            type: "name",
            properties: {
              name: "EPSG:4326",
            },
          };

          //console.log(geoJSON);

          const ts = moment();
          const folder = `staze`;
          const fileName = `staze_${ts.format("YYYYMMDD_HHmm")}.zip`;

          const shpFileDownloadOptions = {
            folder: folder,
            outputType: "blob",
            compression: "DEFLATE",
            types: {
              line: "mylines",
            },
          };

          //@ts-ignore
          shpwriteZip<"LineString">(geoJSON, shpFileDownloadOptions).then(
            (zipData: Blob) => {
              // const file = new Blob(zipData as Blob, { type: "application/zip" });

              fileDownload(zipData, fileName);
            }
          );
        }
      })
      .catch((err) => {
        console.warn(err);
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      });
  };

  const handleDownloadExcel = () => {
    const dc: IDataController = new dataController(model);
    loaderContext.toggleLoading(true);
    dc.GetData("staze?export=shape")
      .then((resp) => {
        if (resp.success) {
          const records = resp.data as DCRecord[];

          const fields = dc.getViewFields("exportExcel");
          const obj = {
            records: records,
            fields: fields,
            filename: "titles.staze",
            t: t,
          };
          exportTableToExcel(obj);
        }
      })
      .catch((err) => {
        console.warn(err);
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      });
  };

  return (
    <AppPage needPermission={[Permissions.Administration]}>
      <Box m={mdUp ? 2 : undefined}>
        <GridContainer>
          <GridItem size={{xs: 12, md: 6}}>
            <Stack direction="column" spacing={mdDown ? 1 : 2} width={"100%"}>
              <Stack
                direction={mdDown ? "column" : "row"}
                spacing={2}
                width={"100%"}
                alignItems={mdDown ? "center" : undefined}
              >
                <Card sx={{ maxWidth: 345 }}>
                  {!mdDown ? (
                    <CardMedia
                      sx={{ height: 140 }}
                      image={MapsImage}
                      title="maps"
                    />
                  ) : null}
                  <CardContent>
                    <Typography gutterBottom variant="h5" component="div">
                      Shapefile
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Preuzmtie podatke o stazama u Shapefile formatu kako bi ih
                      usporedili s vanjskim izvorima informacija.
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={handleDownloadShape}>
                      Download
                    </Button>
                  </CardActions>
                </Card>
                <Card sx={{ maxWidth: 345 }}>
                  {!mdDown ? (
                    <CardMedia
                      sx={{ height: 140 }}
                      image={ExcelImage}
                      title="maps"
                    />
                  ) : null}
                  <CardContent>
                    <Typography gutterBottom variant="h5" component="div">
                      Excel format
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Preuzmite podatke o stazama u Excel formatu kako bi
                      proveli daljnje analize u programu po vlastitom izboru.
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={handleDownloadExcel}>
                      Download
                    </Button>
                  </CardActions>
                </Card>
              </Stack>
              <Card sx={{ width: "100%" }}>
                <ReportsStazeTable />
              </Card>
            </Stack>
          </GridItem>
          <GridItem size={{xs: 12, md: 6}}>
            <Card sx={{ height: "90vh", m: mdDown ? 2 : undefined }}>
              <Typography
                gutterBottom
                variant="h5"
                component="div"
                textAlign="center"
                sx={{ width: "100%" }}
              >
                Učestalost korištenja točaka za rutiranje
              </Typography>
              <ReportsMap />
            </Card>
          </GridItem>
        </GridContainer>
      </Box>
    </AppPage>
  );
}

export default ReportsPage;
